import { useEffect, useMemo, useState } from "react";
import { actionView, type ActionField, type ActionView } from "../../shared/action-call";
import type { ActionCall, ApprovalDTO } from "../../shared/types";
import { useUser } from "../App";
import { get } from "../lib/api";
import { canCheckSend, canSettleRelease, releaseState } from "../lib/approval-text";
import { AgentAvatar } from "./AgentAvatar";
import { formatBytes } from "./Files";
import { Badge, timeAgo } from "./ui";
import { LockedVerdicts, ReleaseOutcome, VerdictBar } from "./Verdicts";

/** Everything the card draws from the call is plain text in here: React writes it as text, so nothing in it runs or loads. */
const plainCls = "scroll-thin max-h-96 overflow-auto rounded-xl bg-surface p-3 text-xs whitespace-pre-wrap break-words ring-1 ring-line";

function Row({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="contents">
      <dt className="text-xs font-medium text-muted sm:pt-0.5">{label}</dt>
      <dd className="min-w-0 break-words text-fg">{value}</dd>
    </div>
  );
}

function Fields({ fields }: { fields: ActionField[] }) {
  if (!fields.length) return null;
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[7rem_minmax(0,1fr)]">
      {fields.map((f) => (
        <div key={f.key} className="contents">
          <dt className="font-mono text-xs break-all text-muted sm:pt-0.5">{f.key}</dt>
          <dd className="min-w-0 break-words whitespace-pre-wrap text-fg">{f.text}</dd>
        </div>
      ))}
    </dl>
  );
}

/** What would really go out, read from the stored bytes: an email, the call's fields, its text, or why it cannot be shown. */
export function ActionContent({ view }: { view: ActionView }) {
  if (view.kind === "empty") return <p className="text-sm text-muted">This call has no body.</p>;
  if (view.kind === "text") return <pre className={plainCls}>{view.text}</pre>;
  if (view.kind === "fields") return <Fields fields={view.fields} />;
  if (view.kind === "unreadable") {
    return (
      <div className="space-y-2">
        <p className="rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">
          This can't be shown: {view.why}. It is {formatBytes(view.bytes)}{view.contentType ? ` of ${view.contentType}` : ""}.
        </p>
        {view.fields && <Fields fields={view.fields} />}
      </div>
    );
  }
  const { email } = view;
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-[7rem_minmax(0,1fr)]">
        <Row label="From" value={email.from} />
        <Row label="To" value={email.to} />
        <Row label="Cc" value={email.cc} />
        <Row label="Bcc" value={email.bcc} />
        <Row label="Reply to" value={email.replyTo} />
        <Row label="Subject" value={email.subject} />
        {email.headers.map((h, i) => <Row key={i} label={h.key} value={h.text} />)}
      </dl>
      {email.text && <pre className={plainCls}>{email.text}</pre>}
      {email.html && (
        <div className="space-y-1">
          <p className="text-xs text-muted">Its HTML, which most mail apps show instead of the text above</p>
          <pre className={plainCls}>{email.html}</pre>
        </div>
      )}
      {!email.text && !email.html && <p className="text-sm text-muted">This email has no text.</p>}
      {email.attachments.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted">
          {email.attachments.map((a, i) => (
            <li key={i} className="break-all">
              Attachment: <span className="font-mono text-fg">{a.name}</span> · {formatBytes(a.bytes)}{a.type ? ` · ${a.type}` : ""}
            </li>
          ))}
        </ul>
      )}
      <Fields fields={view.fields} />
    </div>
  );
}

/**
 * An action an agent proposes, decided in the chat it was filed in: the action's name, the real call read from its stored
 * bytes, the agent's reason labelled as the agent's, and Approve, Request changes or Reject for whoever it is addressed to.
 * Anyone else reads that it exists and who decides it, never what it would send. Plain text from top to bottom.
 */
export function ActionCard({ approval, canDecide, onDecided }: { approval: ApprovalDTO; canDecide: boolean; onDecided?: (updated: ApprovalDTO) => void }) {
  // What we just decided holds until the server's own copy arrives (the websocket, or the next load).
  const [local, setLocal] = useState<ApprovalDTO | null>(null);
  useEffect(() => setLocal(null), [approval]);
  const a = local ?? approval;
  const user = useUser();
  const keep = (updated: ApprovalDTO) => {
    setLocal(updated);
    onDecided?.(updated);
  };
  const shown = a.payload as Partial<ActionCall>;
  // The card as it arrives live holds no body (it stays out of the run's log), so its addressee reads it from the row.
  const [loaded, setLoaded] = useState<ActionCall | null>(null);
  const missing = Boolean(shown.method && shown.bytes && shown.bodyBase64 === undefined);
  useEffect(() => {
    if (missing) get<ApprovalDTO>(`/api/approvals/${a.id}`).then((row) => setLoaded(row.payload as ActionCall)).catch(() => {});
  }, [a.id, missing]);
  const call = (missing ? loaded : shown) ?? {};
  const view = useMemo(() => (call.method ? actionView(call as ActionCall) : null), [a.id, Boolean(call.method)]);
  const asWho = a.ownerUserId === user.id ? "you" : (a.ownerName ?? a.agentName);

  return (
    <div className="card border-l-2 border-l-amber-400/70 p-5 md:p-6">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <AgentAvatar agent={a.agentId} name={a.agentName} size="sm" />
        <Badge tone="amber">Sensitive action</Badge>
        {a.version > 1 && <Badge tone="zinc">version {a.version}</Badge>}
        <span className="text-muted">
          <span className="font-medium text-fg">{a.agentName}</span> wants to do this as {asWho}
        </span>
      </div>
      <p className="mt-2 text-reading leading-6 font-medium tracking-tight break-words">{a.title}</p>
      {view ? (
        <>
          <p className="mt-1 font-mono text-xs break-all text-faint">
            {call.connectorName} · {call.method} {call.path}{call.query}
          </p>
          <div className="mt-3 rounded-xl bg-fill-subtle p-3">
            <ActionContent view={view} />
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-muted">What it would send is for the person it is addressed to.</p>
      )}
      {a.reason && (
        <div className="mt-3">
          <p className="text-xs font-medium text-muted">{a.agentName}'s reason</p>
          <p className="mt-0.5 text-sm break-words whitespace-pre-wrap text-muted">{a.reason}</p>
        </div>
      )}
      {a.status === "pending" ? (
        <div className="mt-4">{canDecide ? <VerdictBar a={a} reviseLabel="Request changes" voice onDecided={(updated) => updated && keep(updated)} /> : <LockedVerdicts a={a} reviseLabel="Request changes" />}</div>
      ) : (
        <ActionOutcome a={a} canAct={canSettleRelease(a, user)} canCheck={canCheckSend(a, user)} onChanged={keep} />
      )}
    </div>
  );
}

/** What became of it, in one line: sent, being sent, not sent and why, rejected, or sent back. */
function ActionOutcome({ a, canAct, canCheck, onChanged }: { a: ApprovalDTO; canAct: boolean; canCheck: boolean; onChanged: (updated: ApprovalDTO) => void }) {
  const state = releaseState(a);
  const by = a.decidedByName ? ` · ${a.decidedByName} · ${timeAgo(a.decidedAt)}` : ` · ${timeAgo(a.decidedAt)}`;
  return (
    <div className="mt-4 border-t border-line pt-3 text-sm">
      {a.status === "approved" && state === "sent" ? (
        <>
          <p className="text-muted">
            <span className="text-success">✓ </span>
            <span className="font-medium text-fg">Sent</span>
            <span className="text-faint">{by}</span>
          </p>
          <ReleaseOutcome a={a} canAct={canAct} canCheck={canCheck} onChanged={onChanged} />
        </>
      ) : a.status === "approved" && !state && a.releaseStartedAt ? (
        <p className="text-muted">Sending…</p>
      ) : a.status === "approved" ? (
        <>
          <p className="text-muted">
            <span className="font-medium text-fg">Approved</span>
            <span className="text-faint">{by}</span>
          </p>
          <ReleaseOutcome a={a} canAct={canAct} canCheck={canCheck} onChanged={onChanged} />
        </>
      ) : a.status === "revision_requested" ? (
        <p className="break-words text-muted">
          <span className="text-faint">↻ </span>
          <span className="font-medium text-fg">Changes requested:</span> {a.decisionNote}
        </p>
      ) : (
        <>
          <p className="text-muted">
            <span className="text-faint">{a.status === "withdrawn" ? "– " : "✕ "}</span>
            <span className="font-medium text-fg">{a.status === "withdrawn" ? "Withdrawn" : "Rejected"}</span>
            <span className="text-faint">{by}</span>
          </p>
          {a.decisionNote && <p className="mt-1 break-words whitespace-pre-wrap text-muted">“{a.decisionNote}”</p>}
          {/* Nothing went, in as many words: this is the one card where that is the point. */}
          <p className="mt-1 text-muted">Nothing was sent.</p>
        </>
      )}
    </div>
  );
}
